import { Popup } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import { getMasterData, getVehicleBlocking } from '../../devir-teslim/api';
import { Button, FormGroup, Input, Label, Row } from 'reactstrap';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import useFormatMessage from '../../../utils/useFormatMessage';

const Blokaj = ({ data, setBlockingState, blockingState, chassisNo }) => {
    const [open, setOpen] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [active, setActive] = useState(null);   // active general blockages by type (order_management.vehicle_blocking)
    const [typeNames, setTypeNames] = useState({});
    const { t } = useFormatMessage();

    useEffect(() => {
        getMasterData().then((md) => setTypeNames(Object.fromEntries((md.blocking_types || []).map((x) => [x.code, x.name_tr])))).catch(() => {});
    }, []);
    useEffect(() => {
        if (!chassisNo) return setActive(null);
        getVehicleBlocking(chassisNo).then(setActive).catch(() => setActive(null));
    }, [chassisNo, data]);

    // Move the blocking state logic to a separate memoized function
    const calculateBlockingState = useCallback((inputData) => {
        if (!Array.isArray(inputData) || inputData.length === 0) {
            return {
                hasBlokaj: false,
                hasSevkBlokaj: false
            };
        }

        return inputData.reduce((acc, item) => {
            if (!item || typeof item !== 'object') return acc;

            const itemData = item.data || {};
            let newState = { ...acc };

            if (item.operation === "BLOKAJ") {
                if (itemData.action === "EKLE") {
                    newState.hasBlokaj = true;
                } else if (itemData.action === "KALDIR") {
                    newState.hasBlokaj = false;
                }
            }

            const sevkBlokajBilgisi = itemData["sevk/blokaj bilgisi"];
            if (sevkBlokajBilgisi === "BLOKAJ") {
                newState.hasSevkBlokaj = true;
            } else if (sevkBlokajBilgisi === "SEVK") {
                newState.hasSevkBlokaj = false;
            }

            return newState;
        }, {
            hasBlokaj: false,
            hasSevkBlokaj: false
        });
    }, []);

    // Move filtering logic to a separate memoized function
    const filterData = useCallback((inputData) => {
        if (!Array.isArray(inputData)) return [];

        return inputData.filter(item => {
            if (!item || !item.data) return false;

            const sevkBlokajBilgisi = item.data["sevk/blokaj bilgisi"];
            return (
                item.operation === "BLOKAJ" ||
                sevkBlokajBilgisi === "BLOKAJ" ||
                sevkBlokajBilgisi === "SEVK"
            );
        });
    }, []);

    useEffect(() => {
        // Process data only if it has changed
        if (!data) return;

        const newBlockingState = calculateBlockingState(data);
        const filteredData = filterData(data);

        // Only update states if they've actually changed
        setDataSource(filteredData);

        // Compare the new blocking state with current before updating
        if (JSON.stringify(newBlockingState) !== JSON.stringify(blockingState)) {
            setBlockingState(newBlockingState);
        }

        // Cleanup function
        return () => {
            setDataSource([]);
        };
    }, [data, calculateBlockingState, filterData, setBlockingState, blockingState]);

    const getDisplayValue = useCallback((rowData) => {
        if (!rowData || !rowData.data) return '';

        if (rowData.operation === "BLOKAJ/SEVK BİLGİSİ") {
            return rowData.data["sevk/blokaj bilgisi"] === "BLOKAJ"
                ? "SEVK BLOKAJI: Eklendi"
                : "SEVK BLOKAJI: Kaldırıldı";
        }

        const sevkBlokajBilgisi = rowData.data["sevk/blokaj bilgisi"];
        if (sevkBlokajBilgisi === "BLOKAJ") {
            return "SEVK BLOKAJI: Eklendi";
        } else if (sevkBlokajBilgisi === "SEVK") {
            return "SEVK BLOKAJI: Kaldırıldı";
        }

        const typ = rowData.data["blokaj tipi"];
        const label = typ ? `GENEL BLOKAJ (${typeNames[typ] || typ})` : 'GENEL BLOKAJ';
        return rowData.data.action === "EKLE"
            ? `${label}: Eklendi`
            : `${typ ? label : 'GENEL BLOKAJ (tüm tipler)'}: Kaldırıldı`;
    }, [typeNames]);

    const calculateFilterExpression = useCallback((filterValue) => {
        return [(dataItem) => {
            const displayValue = getDisplayValue(dataItem);
            return displayValue.toLowerCase().includes(filterValue.toLowerCase());
        }];
    }, [getDisplayValue]);

    return (
        <>
            <div className="mb-2" style={{ fontSize: 13 }}>
                {active
                    ? <span><b className="text-danger">Aktif genel blokaj:</b> {active.type_names.join(', ')} · engellenen işlemler:{' '}
                        {[active.blocks_groupage && 'grupaj', active.blocks_gate_exit && 'kapı çıkışı', active.blocks_transfer && 'transfer'].filter(Boolean).join(', ') || 'yok'}</span>
                    : <span className="text-muted">Aktif genel blokaj yok.</span>}
                {blockingState?.hasSevkBlokaj && <span className="text-danger ml-3"><b>Bayi talebi blokajı</b> (Blokaj/Sevk BLOKAJ)</span>}
            </div>
            <DatagridCustom dataSource={dataSource}>
                <Column caption={t("Dosya")} dataField="file_name" />
                <Column
                    caption={t("Blokaj Tipi")}
                    dataField="operation"
                    calculateDisplayValue={getDisplayValue}
                    calculateFilterExpression={calculateFilterExpression}
                />
                <Column
                    caption={t("Blokaj Açıklama")}
                    calculateCellValue={(r) => r?.data?.aciklamas || r?.data?.aciklama || ''}
                />
                <Column
                    caption={t("Talep Eden")}
                    calculateCellValue={(r) => r?.data?.["talep eden bayi"] || r?.data?.["telep eden bayi"] || ''}
                />
                <Column
                    caption={t("Tarih")}
                    dataField="process_date"
                    dataType="datetime"
                    format="dd/MM/yyyy HH:mm"
                />
                <Column caption={t("İşlemi Yapan")} dataField="username" />
            </DatagridCustom>

            <Popup
                visible={open}
                onHiding={() => setOpen(false)}
                title="Blokaj Ekle"
            >
                <Row>
                    <FormGroup>
                        <Label for="blokajAciklama">Açıklama</Label>
                        <Input
                            type="text"
                            name="aciklama"
                            id="blokajAciklama"
                            placeholder="Açıklama"
                        />
                    </FormGroup>
                    <Row>
                        <Button className="me-2">
                            Kaydet
                        </Button>
                        <Button color="danger" onClick={() => setOpen(false)}>
                            İptal
                        </Button>
                    </Row>
                </Row>
            </Popup>
        </>
    );
};

export default Blokaj;